import type { ParseError, Upload, UploadDetected } from '@tabservice/tabfile-client';
import { Card, LabelValue, LabelValueList, SectionHeader } from '@/components/ui/card';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatCount } from '@/lib/format';

/** Columns in the file that match no field on the chosen form. */
export function UnmappedColumns({ columns }: { columns: string[] }) {
  if (columns.length === 0) return null;
  return (
    <Card>
      <SectionHeader
        title="Ignored columns"
        description="These match no field on this form, so nothing from them is filed."
      />
      <div className="flex flex-wrap gap-2">
        {columns.map((column) => (
          <span key={column} className="rounded-md bg-muted px-2 py-1 text-sm text-gray-3">
            {column}
          </span>
        ))}
      </div>
    </Card>
  );
}

export function ParseErrors({ errors }: { errors: ParseError[] }) {
  if (errors.length === 0) return null;
  return (
    <Card>
      <SectionHeader
        title="Lines with problems"
        description="Line numbers count from the top of the file. Errors must be fixed, warnings are for information."
      />
      <ul className="flex flex-col">
        {errors.map((error, index) => (
          <li
            key={`${error.line ?? index}-${error.message}`}
            className="flex gap-6 border-b border-[color:var(--ts-row-border)] py-3 last:border-b-0"
          >
            <span className="w-20 shrink-0 text-base text-muted-foreground">
              {error.line ? `line ${error.line}` : 'file'}
            </span>
            <span className={error.severity === 'warning' ? 'text-base text-muted-foreground' : 'text-base text-gray-3'}>
              {error.message}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** What a 1220 file declares: form, year and the issuers inside it. */
export function Pub1220Detected({ upload }: { upload: Upload }) {
  const detected: UploadDetected = upload.detected ?? {};
  const issuers = detected.issuers ?? [];
  return (
    <Card>
      <SectionHeader
        title="Read from the file"
        description="The form type and tax year come from the file itself, not from the selection above."
      />
      <LabelValueList className="mb-5">
        <LabelValue label="Form type">{detected.formType ?? 'not found'}</LabelValue>
        <LabelValue label="Tax year">{detected.taxYear ? String(detected.taxYear) : 'not found'}</LabelValue>
        <LabelValue label="Issuers">{formatCount(issuers.length)}</LabelValue>
        <LabelValue label="Records">{formatCount(upload.recordCount ?? 0)}</LabelValue>
      </LabelValueList>

      {(detected.formTypes?.length ?? 0) > 1 ? (
        <p className="mb-5 text-base text-destructive">
          This file holds more than one form type ({detected.formTypes?.join(', ')}). Split it into one file per form
          type and upload them separately.
        </p>
      ) : null}

      {issuers.length > 0 ? (
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>Issuer</TH>
                <TH>Taxpayer ID</TH>
                <TH>Form</TH>
                <TH align="right">Records</TH>
              </tr>
            </THead>
            <TBody>
              {issuers.map((issuer, index) => (
                <TR key={`${issuer.tinMasked ?? index}-${issuer.name ?? index}`}>
                  <TD className="font-medium">{issuer.name ?? 'Name not in the file'}</TD>
                  <TD className="text-muted-foreground">{issuer.tinMasked ?? '**-*******'}</TD>
                  <TD className="text-muted-foreground">{issuer.formType ?? '-'}</TD>
                  <TD numeric>{formatCount(issuer.recordCount ?? 0)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      ) : (
        <p className="text-base text-muted-foreground">No issuer records were found in this file.</p>
      )}
    </Card>
  );
}

/** What an IRIS XML transmission declares, plus any schema violations. */
export function IrisXmlDetected({ upload }: { upload: Upload }) {
  const detected: UploadDetected = upload.detected ?? {};
  const schemaErrors = detected.schemaErrors ?? [];
  return (
    <>
      <Card>
        <SectionHeader
          title="Read from the transmission"
          description="The XML is validated against the IRS schema, then sent under the Tab Service transmitter header."
        />
        <LabelValueList>
          <LabelValue label="Form type">{detected.formType ?? 'not found'}</LabelValue>
          <LabelValue label="Tax year">{detected.taxYear ? String(detected.taxYear) : 'not found'}</LabelValue>
          <LabelValue label="Submissions">{formatCount(detected.submissionCount ?? 0)}</LabelValue>
          <LabelValue label="Records">{formatCount(upload.recordCount ?? 0)}</LabelValue>
        </LabelValueList>
      </Card>

      {schemaErrors.length > 0 ? (
        <Card>
          <SectionHeader
            title="Schema errors"
            description="The IRS would refuse this file. Fix these in the XML and upload it again."
          />
          <ul className="flex flex-col">
            {schemaErrors.map((error) => (
              <li
                key={error}
                className="border-b border-[color:var(--ts-row-border)] py-3 text-base break-words text-destructive last:border-b-0"
              >
                {error}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </>
  );
}
